import { cx } from '@twind/core';
import useFeatureFlags from 'app/hooks/useFeatureFlags';
import React, { HTMLAttributes } from 'react';

export function Heading1({ children, className, id }: { children: React.ReactNode; className?: string; id?: string }) {
  return (
    <h1
      className={cx('heading1', '~(text-xl font-semibold)', className)}
      id={id}
    >
      {children}
    </h1>
  );
}

export function Heading2({ children, className }: { children: React.ReactNode; className?: string }) {
  return <h2 className={cx('heading2', '~(text-lg)', className)}>{children}</h2>;
}

export function Heading3({
  children,
  className,
  ...rest
}: { children: React.ReactNode; className?: string } & HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3
      {...rest}
      className={cx('ostro-heading3', '~(text-base)', className)}
    >
      {children}
    </h3>
  );
}

type TitleProps<T extends React.ElementType = 'h4'> = {
  children: React.ReactNode;
  className?: string;
  component?: T;
} & React.ComponentProps<T>;

export function Title<T extends React.ElementType = 'h4'>({
  children,
  className,
  component: Component = 'h4' as T,
  ...props
}: TitleProps<T>) {
  return (
    <Component
      className={cx('ostro-title', '~(text-sm)', className)}
      {...props}
    >
      {children}
    </Component>
  );
}

type BodyProps<T extends React.ElementType = 'p'> = {
  children: React.ReactNode;
  className?: string;
  component?: T;
} & React.ComponentProps<T>;

export function Body<T extends React.ElementType = 'p'>({
  children,
  className,
  component: Component = 'p' as T,
  ...props
}: BodyProps<T>) {
  const featureFlags = useFeatureFlags();
  return (
    <Component
      className={cx('ostro-body', featureFlags.uiRefreshFontSizeIncrease ? '~(text-sm)' : '~(text-xs)', className)}
      {...props}
    >
      {children}
    </Component>
  );
}

type SubheadingProps<T extends React.ElementType = 'div'> = {
  children: React.ReactNode;
  className?: string;
  component?: T;
} & React.ComponentProps<T>;

export function Subheading<T extends React.ElementType = 'div'>({
  children,
  className,
  component: Component = 'div' as T,
  ...props
}: SubheadingProps<T>) {
  return (
    <Component
      className={cx('ostro-subheading', '~(text-sm font-semibold)', className)}
      {...props}
    >
      {children}
    </Component>
  );
}

type CopyProps<T extends React.ElementType = 'p'> = {
  children?: React.ReactNode;
  className?: string;
  component?: T;
} & React.ComponentProps<T>;

export function Copy<T extends React.ElementType = 'p'>({
  children,
  className,
  component: Component = 'p' as T,
  ...props
}: CopyProps<T>) {
  const featureFlags = useFeatureFlags();
  const fontSize = featureFlags.uiRefreshFontSizeIncrease ? 'sm' : 'xs';
  return (
    <Component
      className={cx('ostro-copy', `~(text-${fontSize} font-medium)`, className)}
      {...props}
    >
      {children}
    </Component>
  );
}
